import { getOrderShortCode } from '@mahawi/eshop-common';
import {
  EWarehouseOrderEventType,
  type IWarehouseOrderChange,
  type IWarehouseOrderEventDataMap,
  type IWarehouseOrderEventItem,
} from '@mahawi/eshop-common/src/types';
import { Space, Typography } from 'antd';
import React from 'react';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import {
  WAREHOUSE_ORDER_CHANGE_FIELD_LABEL,
  WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL,
  WAREHOUSE_ORDER_STATUS_LABEL,
} from './labels';

const { Text } = Typography;

const toText = (value: string | number | null): string =>
  value === null || value === '' ? EMPTY_VALUE : String(value);

function EventDescription({
  event,
}: {
  event: IWarehouseOrderEventItem;
}): React.ReactElement {
  switch (event.type) {
    case EWarehouseOrderEventType.CREATED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.CREATED];

      return (
        <Text>
          {WAREHOUSE_ORDER_STATUS_LABEL[data.status]},{' '}
          {data.totalWithoutTax.toFixed(2)} {data.currencyIsoCode} without VAT
        </Text>
      );
    }
    case EWarehouseOrderEventType.UPDATED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.UPDATED];

      return (
        <Space orientation="vertical" size={SPACING.none}>
          {data.changes.map(
            (change: IWarehouseOrderChange): React.ReactElement => (
              <Text key={change.field}>
                <Text type="secondary">
                  {WAREHOUSE_ORDER_CHANGE_FIELD_LABEL[change.field]}:
                </Text>{' '}
                {toText(change.from)} → {toText(change.to)}
              </Text>
            ),
          )}
        </Space>
      );
    }
    case EWarehouseOrderEventType.PAIRED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.PAIRED];

      return (
        <Space size={SPACING.xs}>
          <Link
            to={`/order/${data.orderUuid}`}
            label={getOrderShortCode(data.orderUuid)}
            openInNewTab
          />
          <Text type="secondary">
            {WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL[data.source]}
          </Text>
        </Space>
      );
    }
    case EWarehouseOrderEventType.UNPAIRED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.UNPAIRED];

      return (
        <Link
          to={`/order/${data.orderUuid}`}
          label={getOrderShortCode(data.orderUuid)}
          openInNewTab
        />
      );
    }
    case EWarehouseOrderEventType.SYNC_COMPLETED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.SYNC_COMPLETED];

      return (
        <Text>
          Changed since {data.changedSince}: {data.fetched} fetched,{' '}
          {data.created} new, {data.updated} updated, {data.unchanged}{' '}
          unchanged, {data.paired} paired
        </Text>
      );
    }
    case EWarehouseOrderEventType.SYNC_FAILED: {
      const data =
        event.data as IWarehouseOrderEventDataMap[EWarehouseOrderEventType.SYNC_FAILED];

      return (
        <Text type="danger">
          Changed since {data.changedSince}: {data.error}
        </Text>
      );
    }
  }
}

export default EventDescription;
